import { config } from 'config'

import { type Fulfillment } from './to-fulfillment'
import { type FulfillmentYandex } from './to-fulfillment-detail'

/** Поисковикам нужны полные адреса: относительная ссылка для них ничего не значит. */
const absolute = (path: string) => `${config.frontendUrl}${path}`

/** Путь до страницы: поисковик показывает его вместо голого адреса под заголовком. */
export const breadcrumbSchema = (items: { name: string; path: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absolute(item.path),
  })),
})

/** Подборка компаний: так поисковик понимает, что страница про список, а не про одну организацию. */
export const fulfillmentListSchema = (name: string, items: { name: string; slug: string }[]) => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name,
  numberOfItems: items.length,
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    url: absolute(`/fulfillment/${item.slug}`),
  })),
})

/**
 * Карточка компании. Отдаём совокупную оценку — ту же, что напечатана в шапке карточки:
 * разметка обязана совпадать с содержимым страницы, иначе поисковик считает её обманом
 * и убирает звёзды у всего сайта.
 */
export const fulfillmentSchema = (fulfillment: Fulfillment, yandex: FulfillmentYandex | null) => {
  const total = fulfillment.ratings.total
  const rating = total ? Number(total.value.toFixed(1)) : null
  const ratingCount = total?.reviewCount ?? null
  // Первый склад считаем главным адресом компании: он же стоит первым в карточке.
  const mainLocation = fulfillment.locations[0]
  const address = yandex?.address
    ? { '@type': 'PostalAddress', streetAddress: yandex.address }
    : mainLocation
      ? {
          '@type': 'PostalAddress',
          addressLocality: mainLocation.city.name,
          addressRegion: mainLocation.city.regionName,
          ...(mainLocation.address ? { streetAddress: mainLocation.address } : {}),
        }
      : null

  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: fulfillment.name,
    description: fulfillment.description,
    url: absolute(`/fulfillment/${fulfillment.slug}`),
    ...(fulfillment.coverFileId ? { image: absolute(`/api/server/fulfillment/${fulfillment.slug}/cover`) } : {}),
    ...(address ? { address } : {}),
    ...(fulfillment.websiteUrl ? { sameAs: [fulfillment.websiteUrl] } : {}),
    ...(rating !== null && ratingCount !== null && ratingCount > 0
      ? {
          aggregateRating: {
            '@type': 'AggregateRating',
            ratingValue: rating,
            reviewCount: ratingCount,
            bestRating: 5,
            worstRating: 1,
          },
        }
      : {}),
  }
}
